<template>
  <section class="admin-index-page">
    <div class="admin-page-title mb-3">{{ $t("admin_dashboard") }}</div>
    <KpiCards class="mb-3" :items="kpiItems" :loading="!summaryCards.length" :skeleton="4" />

    <v-row class="hottest-stores-row">
      <v-col cols="12" class="hottest-stores-section">
        <div
          class="hottest-stores-header d-flex justify-space-between align-center flex-wrap"
        >
          <div class="hottest-stores-title mb-0">
            {{ $t("hottest_stores") || "Hottest Stores" }}
          </div>
          <DatePicker
            :dense="true"
            :placeholder="false"
            label="date"
            v-model="filterDate"
            class="date-filter-inline"
          />
        </div>

        <div class="store-grid-container">
          <v-row dense>
            <v-col
              v-for="(store, index) in bestSellerList"
              :key="store.id"
              cols="12"
              sm="6"
              md="4"
              lg="2"
              xl="2"
              class="store-col"
            >
              <v-card
                rounded="xl"
                class="store-card"
                :class="{
                  'top-store': index < 3,
                  'rank-1': index === 0,
                  'rank-2': index === 1,
                  'rank-3': index === 2,
                }"
                elevation="0"
                tabindex="0"
                role="button"
                :aria-label="store.name"
                @click="handleStoreClick(store)"
                @keydown.enter.prevent="handleStoreClick(store)"
                @keydown.space.prevent="handleStoreClick(store)"
              >
                <div class="store-rank-badge">
                  <v-chip
                    v-if="index < 3"
                    :color="getRankColor(index)"
                    dark
                    x-small
                    class="rank-badge-chip"
                  >
                    <v-icon x-small left>{{ getRankIcon(index) }}</v-icon>
                    {{ index + 1 }}
                  </v-chip>
                  <v-chip v-else x-small outlined class="rank-badge-chip">
                    #{{ index + 1 }}
                  </v-chip>
                </div>
                <v-card-text class="pa-4 text-center">
                  <div class="store-logo-wrapper">
                    <v-avatar size="60" class="store-logo-avatar">
                      <v-img
                        v-if="getStoreLogo(store.logo) && !logoFailed[store.id]"
                        :src="getStoreLogo(store.logo)"
                        width="100%"
                        height="100%"
                        contain
                        @error="$set(logoFailed, store.id, true)"
                      >
                        <template v-slot:placeholder>
                          <v-icon color="grey lighten-1" size="30"
                            >fal fa-store</v-icon
                          >
                        </template>
                      </v-img>
                      <v-icon v-else color="grey lighten-1" size="30"
                        >fal fa-store</v-icon
                      >
                    </v-avatar>
                  </div>
                  <div class="store-name mt-2">{{ store.name }}</div>
                  <div class="store-meta mt-2">
                    <v-chip
                      :color="getStoreTypeBadge(store.store_type_code).color"
                      x-small
                      outlined
                      class="store-type-chip"
                    >
                      {{ getStoreTypeBadge(store.store_type_code).label }}
                    </v-chip>
                  </div>
                  <div class="store-tickets mt-3">
                    <div class="ticket-count">
                      <v-icon x-small color="primary" class="mr-1"
                        >fal fa-ticket-alt</v-icon
                      >
                      <span class="ticket-number">{{
                        formatTicketCount(store.ticket_count)
                      }}</span>
                    </div>
                    <div class="ticket-label mt-1">
                      {{ $t("tickets") || "Tickets" }}
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </div>
        <div
          v-if="!bestSellerList || bestSellerList.length === 0"
          class="no-stores"
        >
          <v-icon color="grey lighten-1" size="48">fal fa-store-slash</v-icon>
          <div class="no-stores-text">
            {{ $t("no_stores_found") || "No stores found" }}
          </div>
        </div>
      </v-col>
      <v-col cols="12">
        <v-card
          rounded="lg"
          flat
          outlined
          elevation="0"
          class="admin-panel-card"
        >
          <v-card-title class="pa-4 pb-2">
            {{ $t("store_performance") }}
          </v-card-title>
          <v-card-text class="pt-0">
            <apexchart
              v-if="series.length && series[0].data.length"
              width="100%"
              height="495"
              type="area"
              :options="chartOptions"
              :series="series"
            >
            </apexchart>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <custom-dialog
      v-model="storeDetailDialog"
      title="store_detail"
      :footer="false"
      width="950px"
    >
      <template v-slot:activator>
        <div class="store-detail-wrapper">
          <div v-if="storeDetailLoading" class="text-center py-12">
            <v-progress-circular indeterminate color="primary" size="64" />
            <div class="mt-4 text-subtitle-1 grey--text">
              {{ $t("loading_store_details") }}
            </div>
          </div>
          <div v-else-if="selectedStore" class="store-detail-content">
            <!-- Header Section with Logo and Basic Info -->
            <v-card flat class="mb-4 store-header-card">
              <v-card-text class="pa-5">
                <v-row align="center">
                  <v-col cols="auto">
                    <v-avatar size="80" class="store-detail-avatar">
                      <v-img
                        v-if="getStoreLogo(selectedStore.logo)"
                        :src="getStoreLogo(selectedStore.logo)"
                        contain
                      >
                        <template v-slot:placeholder>
                          <v-icon color="grey" size="40">fal fa-store</v-icon>
                        </template>
                      </v-img>
                      <v-icon v-else color="grey" size="40"
                        >fal fa-store</v-icon
                      >
                    </v-avatar>
                  </v-col>
                  <v-col>
                    <div class="text-h5 font-weight-bold mb-1">
                      {{ selectedStore.name }}
                    </div>
                    <div class="d-flex align-center flex-wrap ga-2 mb-2">
                      <v-chip
                        x-small
                        outlined
                        color="grey darken-2"
                        class="mr-1"
                      >
                        <v-icon x-small left>fal fa-barcode</v-icon>
                        {{ selectedStore.code }}
                      </v-chip>
                      <v-chip
                        x-small
                        :color="
                          getStoreTypeBadge(selectedStore.store_type_code).color
                        "
                        dark
                        class="mr-1"
                      >
                        <v-icon x-small left>
                          {{
                            getStoreTypeBadge(selectedStore.store_type_code).icon
                          }}
                        </v-icon>
                        {{
                          getStoreTypeBadge(selectedStore.store_type_code).label
                        }}
                      </v-chip>
                      <v-chip
                        x-small
                        :color="selectedStore.is_active ? 'success' : 'error'"
                        dark
                        class="mr-1"
                      >
                        <v-icon x-small left>
                          {{
                            selectedStore.is_active
                              ? "fal fa-check-circle"
                              : "fal fa-times-circle"
                          }}
                        </v-icon>
                        {{
                          selectedStore.is_active
                            ? $t("active")
                            : $t("inactive")
                        }}
                      </v-chip>
                      <v-chip
                        v-if="selectedStore.is_test_store"
                        x-small
                        color="warning"
                        dark
                      >
                        <v-icon x-small left>fal fa-flask</v-icon>
                        {{ $t("test_store") }}
                      </v-chip>
                    </div>
                    <div class="text-body-2 grey--text darken-1">
                      <v-icon x-small color="grey darken-1"
                        >fal fa-map-marker-alt</v-icon
                      >
                      {{ selectedStore.address || $t("no_address_provided") }}
                    </div>
                    <div
                      v-if="selectedStore.mobile_no"
                      class="text-body-2 grey--text darken-1 mt-1"
                    >
                      <v-icon x-small color="grey darken-1"
                        >fal fa-mobile</v-icon
                      >
                      {{ selectedStore.mobile_no }}
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Statistics Grid -->
            <v-row dense class="mb-4">
              <v-col cols="6" sm="4" md="2">
                <v-card flat class="stat-card">
                  <v-card-text class="pa-3 text-center">
                    <v-icon color="primary" size="28" class="mb-2"
                      >fal fa-cube</v-icon
                    >
                    <div class="text-h6 font-weight-bold primary--text">
                      {{ selectedStore.products_count }}
                    </div>
                    <div class="text-caption grey--text">
                      {{ $t("products") }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="6" sm="4" md="2">
                <v-card flat class="stat-card">
                  <v-card-text class="pa-3 text-center">
                    <v-icon color="success" size="28" class="mb-2"
                      >fal fa-th</v-icon
                    >
                    <div class="text-h6 font-weight-bold success--text">
                      {{ selectedStore.categories_count }}
                    </div>
                    <div class="text-caption grey--text">
                      {{ $t("categories") }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="6" sm="4" md="2">
                <v-card flat class="stat-card">
                  <v-card-text class="pa-3 text-center">
                    <v-icon color="info" size="28" class="mb-2"
                      >fal fa-ticket-alt</v-icon
                    >
                    <div class="text-h6 font-weight-bold info--text">
                      {{ selectedStore.tickets_count }}
                    </div>
                    <div class="text-caption grey--text">
                      {{ $t("tickets") }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="6" sm="4" md="2">
                <v-card flat class="stat-card">
                  <v-card-text class="pa-3 text-center">
                    <v-icon color="warning" size="28" class="mb-2"
                      >fal fa-users</v-icon
                    >
                    <div class="text-h6 font-weight-bold warning--text">
                      {{ selectedStore.stores_user_count }}
                    </div>
                    <div class="text-caption grey--text">{{ $t("users") }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="6" sm="4" md="2">
                <v-card flat class="stat-card">
                  <v-card-text class="pa-3 text-center">
                    <v-icon color="secondary" size="28" class="mb-2"
                      >fal fa-user-friends</v-icon
                    >
                    <div class="text-h6 font-weight-bold secondary--text">
                      {{ selectedStore.customers_count }}
                    </div>
                    <div class="text-caption grey--text">
                      {{ $t("customers") }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="6" sm="4" md="2">
                <v-card flat class="stat-card">
                  <v-card-text class="pa-3 text-center">
                    <v-icon color="primary darken-1" size="28" class="mb-2"
                      >fal fa-code-branch</v-icon
                    >
                    <div
                      class="text-h6 font-weight-bold primary--text text--darken-1"
                    >
                      {{ selectedStore.branches_count }}
                    </div>
                    <div class="text-caption grey--text">
                      {{ $t("branches") }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Package & Subscription -->
            <v-card flat class="mb-4" outlined>
              <v-card-title
                class="text-subtitle-1 font-weight-bold pb-2 grey lighten-4"
              >
                <v-icon left color="primary">fal fa-box</v-icon>
                {{ $t("package_subscription") }}
              </v-card-title>
              <v-card-text class="pt-3">
                <v-row dense>
                  <v-col cols="12" sm="6" md="3">
                    <div class="detail-item">
                      <div class="detail-label">
                        <v-icon x-small color="grey"
                          >fal fa-calendar-check</v-icon
                        >
                        {{ $t("start_date") }}
                      </div>
                      <div class="detail-value">
                        {{ formatDate(selectedStore.start_date) }}
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <div class="detail-item">
                      <div class="detail-label">
                        <v-icon x-small color="grey"
                          >fal fa-calendar-times</v-icon
                        >
                        {{ $t("end_date") }}
                      </div>
                      <div class="detail-value">
                        {{ formatDate(selectedStore.end_date) }}
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <div class="detail-item">
                      <div class="detail-label">
                        <v-icon x-small color="grey">fal fa-clock</v-icon>
                        {{ $t("days_remaining") }}
                      </div>
                      <div class="detail-value">
                        <v-chip x-small color="primary" dark>
                          {{ selectedStore.day }} {{ $t("days") }}
                        </v-chip>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <div class="detail-item">
                      <div class="detail-label">
                        <v-icon x-small color="grey">fal fa-box-open</v-icon>
                        {{ $t("package_id") }}
                      </div>
                      <div class="detail-value">
                        {{ selectedStore.package_id || "-" }}
                      </div>
                    </div>
                  </v-col>
                </v-row>

                <v-divider class="my-3" />

                <div class="text-caption font-weight-bold grey--text mb-2">
                  {{ $t("usage_limits") }}
                </div>
                <v-row dense>
                  <v-col cols="12" sm="6" md="4">
                    <div class="detail-item">
                      <div class="detail-label">
                        <v-icon x-small color="grey">fal fa-cube</v-icon>
                        {{ $t("product_limit") }}
                      </div>
                      <div class="detail-value">
                        {{ selectedStore.product_count }}
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <div class="detail-item">
                      <div class="detail-label">
                        <v-icon x-small color="grey">fal fa-users</v-icon>
                        {{ $t("user_limit") }}
                      </div>
                      <div class="detail-value">
                        {{ selectedStore.user_count }}
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <div class="detail-item">
                      <div class="detail-label">
                        <v-icon x-small color="grey">fal fa-code-branch</v-icon>
                        {{ $t("branch_limit") }}
                      </div>
                      <div class="detail-value">
                        {{ selectedStore.branch_count }}
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Currency & Financial Settings -->
            <v-card flat class="mb-4" outlined>
              <v-card-title
                class="text-subtitle-1 font-weight-bold pb-2 grey lighten-4"
              >
                <v-icon left color="success">fal fa-dollar-sign</v-icon>
                {{ $t("currency_financial") }}
              </v-card-title>
              <v-card-text class="pt-3">
                <v-row dense>
                  <v-col cols="12" sm="6" md="3">
                    <div class="detail-item">
                      <div class="detail-label">
                        <v-icon x-small color="grey">fal fa-money-bill</v-icon>
                        {{ $t("base_currency") }}
                      </div>
                      <div class="detail-value">
                        <v-chip x-small color="primary" dark>
                          {{ selectedStore.currency }}
                        </v-chip>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <div class="detail-item">
                      <div class="detail-label">
                        <v-icon x-small color="grey">fal fa-percentage</v-icon>
                        {{ $t("tax_rate") }}
                      </div>
                      <div class="detail-value">
                        {{ selectedStore.tax }}%
                        <v-chip
                          x-small
                          :color="
                            selectedStore.tax_include ? 'success' : 'grey'
                          "
                          dark
                          class="ml-1"
                        >
                          {{
                            selectedStore.tax_include
                              ? $t("included")
                              : $t("excluded")
                          }}
                        </v-chip>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <div class="detail-item">
                      <div class="detail-label">
                        <v-icon x-small color="grey">fal fa-coins</v-icon>
                        {{ $t("thb_rate") }}
                      </div>
                      <div class="detail-value">{{ selectedStore.thb }}</div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <div class="detail-item">
                      <div class="detail-label">
                        <v-icon x-small color="grey">fal fa-dollar-sign</v-icon>
                        {{ $t("usd_rate") }}
                      </div>
                      <div class="detail-value">{{ selectedStore.usd }}</div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <div class="detail-item">
                      <div class="detail-label">
                        <v-icon x-small color="grey">fal fa-star</v-icon>
                        {{ $t("price_per_point") }}
                      </div>
                      <div class="detail-value">
                        {{ selectedStore.price_per_point }}
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <div class="detail-item">
                      <div class="detail-label">
                        <v-icon x-small color="grey">fal fa-undo</v-icon>
                        {{ $t("return_days") }}
                      </div>
                      <div class="detail-value">
                        {{ selectedStore.return_day }} {{ $t("days") }}
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <div class="detail-item">
                      <div class="detail-label">
                        <v-icon x-small color="grey"
                          >fal fa-map-marker-radius</v-icon
                        >
                        {{ $t("radius") }}
                      </div>
                      <div class="detail-value">
                        {{ selectedStore.radius }} km
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Footer Text if exists -->
            <v-card
              v-if="selectedStore.footer_text"
              flat
              class="mt-4 store-footer-card"
              outlined
            >
              <v-card-text class="pa-3">
                <div class="text-caption font-weight-bold grey--text mb-1">
                  <v-icon x-small color="grey">fal fa-info-circle</v-icon>
                  {{ $t("footer_text") }}
                </div>
                <div class="text-body-2">{{ selectedStore.footer_text }}</div>
              </v-card-text>
            </v-card>
          </div>
          <div v-else class="py-8 text-center grey--text">
            <v-icon size="48" color="grey lighten-1">fal fa-store-slash</v-icon>
            <div class="mt-2">
              {{ $t("no_stores_found") || "No store selected" }}
            </div>
          </div>
        </div>
      </template>
    </custom-dialog>
    <!-- AppLoading -->
    <app-loading :overlay="overlay" />
  </section>
</template>

<script>
import AppCardTitle from "@/components/expense/card-title";
import AppLoading from "@/components/AppLoading";
import DatePicker from "@/components/global/DatePicker";
import CustomDialog from "@/components/customDialog";
import { mapGetters, mapActions, mapMutations } from "vuex";
import { myMixin } from "@/mixin/main";

export default {
  components: { AppCardTitle, DatePicker, AppLoading, CustomDialog },
  mixins: [myMixin],

  data: () => ({
    filterDate: new Date().toISOString().substr(0, 10),
    storeDetailDialog: false,
    logoFailed: {},
    selectedStore: null,
    storeDetailLoading: false,
    chartOptions: {
      chart: {
        type: "area",
        zoom: {
          enabled: true,
        },
        toolbar: {
          show: false,
        },
      },
      colors: ["#5b39c9"],
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.35,
          opacityTo: 0.05,
          stops: [0, 90, 100],
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "smooth",
        width: 2.5,
        colors: ["#5b39c9"],
      },
      markers: {
        size: 3,
        colors: ["#5b39c9"],
        strokeColors: "#ffffff",
        strokeWidth: 2,
        hover: {
          size: 5,
        },
      },
      title: {
        text: "",
        show: false,
      },
      labels: [
        "jan",
        "feb",
        "mar",
        "apr",
        "may",
        "jun",
        "jul",
        "aug",
        "sep",
        "oct",
        "nov",
        "dec",
      ],

      yaxis: {
        opposite: true,
      },
    },
    series: [],
  }),

  async created() {
    await this.fetchItem();
    await this.fetchSummaryDetail();
  },
  watch: {
    summary(val) {
      const res = Help.pluck(val, "total");
      this.series = [
        {
          name: "Total Sales",
          data: res,
        },
      ];
    },
    filterDate(newVal, oldVal) {
      if (newVal !== oldVal && oldVal !== undefined) {
        this.fetchSummaryDetail();
      }
    },
    storeDetailDialog(val) {
      if (!val) {
        this.selectedStore = null;
      }
    },
  },
  computed: {
    kpiItems() {
      return (this.summaryCards || []).map((item, index) => ({
        key: index,
        label: this.$t(item.title || item.text),
        icon: item.icon,
        value: item.total ?? 0,
        note: item.description ? this.$t(item.description) : "",
      }));
    },
    ...mapGetters({
      summary: "index/summary",
      details: "index/summaryDetails",
      overlay: "index/overlay",
    }),
    bestSellerList() {
      return this.details && this.details.bestSeller
        ? this.details.bestSeller
        : [];
    },
    summaryCards() {
      const list =
        this.details && this.details.summary ? this.details.summary : [];
      if (!list.length) return [];

      const fallbackMeta = [
        {
          title: "total_store",
          icon: "fal fa-store",
          description: "all_stores",
        },
        {
          title: "total_product",
          icon: "fal fa-box",
          description: "all_products",
        },
        {
          title: "total_user",
          icon: "fal fa-users",
          description: "total_users",
        },
        {
          title: "total_order",
          icon: "fal fa-shopping-cart",
          description: "completed_orders",
        },
      ];

      return list.slice(0, 4).map((item, idx) => {
        const meta = fallbackMeta[idx] || {};
        return {
          title: item.title || item.text || meta.title,
          total: item.total !== undefined ? item.total : 0,
          icon:
            item.icon && item.icon.startsWith("fal")
              ? item.icon
              : meta.icon || "fal fa-chart-line",
          description: item.description || meta.description,
        };
      });
    },
  },

  methods: {
    ...mapActions({
      fetch: "index/fetch",
    }),
    ...mapMutations({
      SET_OVERLAY: "index/SET_OVERLAY",
    }),

    async fetchItem(val) {
      let url = `admin_summaries`;
      if (val) {
        url = `admin_summaries?q=${val}`;
      }
      const body = {
        that: this,
        url: url,
        commit: "SET_SUMMARY",
      };
      await this.fetch(body);
    },
    async fetchSummaryDetail() {
      this.SET_OVERLAY(true);
      try {
        const dateParam = this.filterDate ? `?date=${this.filterDate}` : "";
        const body = {
          that: this,
          url: `admin_summary${dateParam}`,
          commit: "SET_REPORT_DETAILS",
        };
        await this.fetch(body);
      } finally {
        this.SET_OVERLAY(false);
      }
    },

    async onDateChange() {
      await this.fetchSummaryDetail();
    },

    async filter(val) {},

    async handleStoreClick(store) {
      if (!store || !store.id) {
        console.warn("[HottestStores] Missing store id", store);
        return;
      }
      this.$router.push({
        name: "admin_store_detail",
        params: { store_id: store.id },
      });
    },

    async fetchStoreDetail(storeId) {
      this.storeDetailLoading = true;
      try {
        const { data } = await this.$http.get(`store_detail/${storeId}`);
        if (data && data.status && data.data) {
          this.selectedStore = data.data;
          this.storeDetailDialog = true;
          console.log("[HottestStores] store_detail response:", data);
        } else {
          console.warn(
            "[HottestStores] Unexpected store_detail payload:",
            data,
          );
        }
      } catch (error) {
        console.error(
          "[HottestStores] Failed to fetch store_detail:",
          storeId,
          error,
        );
      } finally {
        this.storeDetailLoading = false;
      }
    },

    getStoreLogo(logo) {
      if (!logo || logo === null || logo === undefined) {
        return null;
      }
      // Check if it's a default logo
      if (
        logo.includes("default.png") ||
        logo.includes("default") ||
        logo === "stores/default.png"
      ) {
        return null;
      }
      // If it's already a full URL, return as is
      if (logo.includes("http://") || logo.includes("https://")) {
        return logo;
      }
      // Otherwise, construct the full URL
      const baseURL = process.env.VUE_APP_BASE_URL;
      return `${baseURL}/uploads/${logo}`;
    },

    getStoreTypeBadge(storeTypeCode) {
      const type = (storeTypeCode || "").toUpperCase();
      if (type === "RESTAURANT") {
        return {
          color: "amber darken-2",
          label: this.$t("restaurant") || "Restaurant",
          icon: "fal fa-utensils",
        };
      }
      if (type === "BUFFET") {
        return {
          color: "secondary",
          label: this.$t("store_type_buffet") || "Buffet",
          icon: "fal fa-layer-group",
        };
      }
      return {
        color: "primary",
        label: this.$t("pos") || "POS",
        icon: "fal fa-money-bill-wave",
      };
    },

    formatTicketCount(count) {
      if (count === null || count === undefined) {
        return "0";
      }
      return Help.formatNumber ? Help.formatNumber(count) : count.toString();
    },

    getRankColor(index) {
      if (index === 0) return "amber darken-2";
      if (index === 1) return "blue-grey";
      if (index === 2) return "brown lighten-1";
      return "primary";
    },

    getRankIcon(index) {
      if (index === 0) return "fas fa-trophy";
      if (index === 1) return "fas fa-medal";
      if (index === 2) return "fas fa-award";
      return "fal fa-star";
    },

    formatDate(date) {
      if (!date) return "-";
      const d = new Date(date);
      return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    formatBoolean(value) {
      if (value === null || value === undefined) {
        return this.$t("no") || "No";
      }
      return value ? this.$t("yes") || "Yes" : this.$t("no") || "No";
    },
  },
};
</script>

<style scoped>
/*
 * Light mode only: page canvas matches mobile light grouped background (~iOS).
 * Scoped to this component; dark mode unchanged.
 */
.theme--light .admin-index-page {
  background-color: transparent;
  min-height: calc(100vh - 72px);
  padding-bottom: 24px;
  box-sizing: border-box;
}

.admin-page-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -0.01em;
}

.theme--dark .admin-page-title {
  color: #f1f5f9;
}

.hottest-stores-row {
  margin-bottom: 8px;
}

.hottest-stores-header {
  gap: 12px;
  margin-bottom: 16px;
}

.hottest-stores-title {
  font-size: 1rem;
  font-weight: 600;
  color: #334155;
  line-height: 1.35;
}

.theme--dark .hottest-stores-title {
  color: #cbd5e1;
}

.store-grid-container {
  overflow: visible;
}

.store-col {
  padding: 6px !important;
}

/* Ensure 5 cards per row on xl screens and above */
@media (min-width: 1904px) {
  .store-col {
    flex: 0 0 20% !important;
    max-width: 20% !important;
  }
}

@media (min-width: 1264px) and (max-width: 1903px) {
  .store-col {
    flex: 0 0 20% !important;
    max-width: 20% !important;
  }
}

.store-card.v-card.v-sheet {
  position: relative;
  border-radius: 16px !important;
  transition: border-color 0.15s ease;
  cursor: pointer;
  min-height: 220px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: none !important;
  outline: none;
  background: #ffffff !important;
  background-color: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-sizing: border-box;
}

.theme--light .store-card.v-card.v-sheet,
.theme--light .store-card.top-store {
  background: #ffffff !important;
  background-color: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
}

.theme--dark .store-card.v-card.v-sheet,
.theme--dark .store-card.top-store {
  background: #1e1e2d !important;
  background-color: #1e1e2d !important;
  border: 1px solid #2d3148 !important;
}

.store-card:focus-visible {
  outline: 2px solid rgba(91, 57, 201, 0.95);
  outline-offset: 2px;
}

.store-detail-wrapper {
  max-height: 75vh;
  overflow-y: auto;
  padding: 4px;
}

.store-detail-content {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.store-header-card,
.stat-card,
.store-footer-card {
  border-radius: 12px;
  background: #fff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  transition: border-color 0.2s ease;
}

.store-detail-avatar {
  border: 1px solid #e2e8f0;
  box-shadow: none !important;
}

.stat-card:hover {
  border-color: #cbd5e1 !important;
}

.theme--dark .store-header-card,
.theme--dark .stat-card,
.theme--dark .store-footer-card {
  background: #1e1e2d !important;
  border-color: #2d3148 !important;
}
.theme--dark .store-detail-avatar {
  border-color: #2d3148;
}

.detail-item {
  display: flex;
  flex-direction: column;
  padding: 6px 0;
}

.detail-label {
  font-size: 0.7rem;
  color: rgba(0, 0, 0, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
  font-weight: 600;
}

.detail-value {
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.87);
  font-weight: 500;
}

.feature-section {
  margin-bottom: 16px;
}

.package-card,
.financial-card,
.features-card,
.footer-text-card {
  border-radius: 12px;
}

.store-detail-wrapper::-webkit-scrollbar {
  width: 8px;
}

.store-detail-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.store-detail-wrapper::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.store-detail-wrapper::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.store-card:hover {
  border-color: #cbd5e1 !important;
  box-shadow: none !important;
}

.theme--light .store-card:hover {
  border-color: rgba(91, 57, 201, 0.45) !important;
}

.theme--dark .store-card:hover {
  border-color: #3b4261 !important;
}

.theme--light .store-logo-avatar {
  border: 1px solid #f1f5f9 !important;
  background-color: #f8fafc !important;
}

@media (prefers-reduced-motion: reduce) {
  .store-card {
    transition: border-color 0.15s ease;
  }
}

.store-card.top-store {
  border-width: 1px !important;
}

.store-card.rank-1,
.store-card.rank-2,
.store-card.rank-3 {
  border-color: #e2e8f0 !important;
}

.theme--dark .store-card.rank-1,
.theme--dark .store-card.rank-2,
.theme--dark .store-card.rank-3 {
  border-color: #2d3148 !important;
}

.store-rank-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
}

.rank-badge-chip {
  font-weight: bold;
  height: 20px;
}

.store-logo-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 8px;
}

.store-logo-avatar {
  border: thin solid rgba(255, 255, 255, 0.12);
}

.store-name {
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.3;
  min-height: 32px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
  line-clamp: 2;
}

.date-filter-inline {
  max-width: 180px;
  flex-shrink: 0;
}

.date-filter-inline >>> .v-input {
  margin-top: 0;
  padding-top: 0;
}

.date-filter-inline >>> .v-input__control {
  min-height: 32px;
}

.date-filter-inline >>> .v-text-field__details {
  display: none;
}

/* Fix date picker menu positioning - open from right side */
.date-filter-inline >>> .v-menu {
  position: relative;
}

.date-filter-inline >>> .v-menu__content {
  left: auto !important;
  right: 0 !important;
  transform-origin: top right !important;
  margin-right: 0 !important;
}

.date-filter-inline >>> .v-menu__content.v-menu__content--fixed {
  position: fixed !important;
}

/* Ensure menu doesn't go off screen */
.date-filter-inline >>> .v-menu__content {
  max-width: calc(100vw - 20px);
}

@media (max-width: 960px) {
  .date-filter-inline {
    max-width: 150px;
  }

  .hottest-stores-header {
    flex-wrap: wrap;
  }

  .date-filter-inline {
    margin-top: 8px;
    width: 100%;
    max-width: 100%;
  }
}

.store-meta {
  display: flex;
  align-items: center;
  justify-content: center;
}

.store-type-chip {
  font-size: 10px;
  height: 18px;
}

.store-tickets {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: auto;
}

.ticket-count {
  display: flex;
  align-items: center;
  justify-content: center;
}

.ticket-number {
  font-size: 16px;
  font-weight: 700;
  color: var(--v-primary-base);
}

.ticket-label {
  font-size: 10px;
  color: rgba(0, 0, 0, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.no-stores {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  grid-column: 1 / -1;
}

.no-stores-text {
  margin-top: 16px;
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
}

@media (max-width: 960px) {
  .store-name {
    font-size: 12px;
    min-height: 28px;
  }

  .ticket-number {
    font-size: 14px;
  }

  .store-logo-avatar {
    width: 50px !important;
    height: 50px !important;
  }
}

@media (max-width: 600px) {
  .store-col {
    flex: 0 0 50% !important;
    max-width: 50% !important;
  }
}
</style>
